import type { BlockCardDefinition, ParagraphDefinition } from '@atlaskit/adf-schema';

import type { PersonElementType } from '../../layout/types';
import type { AdfContentNode, TagConverter } from '../types';

/**
 * Card converters for various Lumina card types.
 * These are currently stubs that return undefined, which will cause them to fall back
 * to paragraph conversion in the main dispatcher.
 *
 * TODO: Implement proper ADF conversion for each card type as needed.
 */

export const convertCarousel: TagConverter = () => undefined;
export const convertActionCard: TagConverter = () => undefined;
export const convertProjectHighlightCard: TagConverter = () => undefined;
export const convertContactCard: TagConverter = () => undefined;

/**
 * Converts a Lumina people-card tag to ADF nodes representing a person.
 *
 * - With url: Returns a blockCard linking to the person's profile (preferred)
 * - Without url: Returns paragraphs with bold name and details (missing fields are skipped)
 *
 * Example output with url:
 *   blockCard → renders as rich profile card
 *
 * Example fallback output (no url):
 *   paragraph: **Jane Smith**
 *   paragraph: Senior Engineer / Sydney, Australia / jane.smith@example.com
 */
// TAG_CONVERTERS guarantees this only receives people-card elements
export const convertPeopleCard = ((luminaNode: PersonElementType): AdfContentNode | undefined => {
	const attrs = luminaNode.attributes ?? {};
	const { name, job_title: jobTitle, location, email, url } = attrs;

	// If URL is present, return a blockCard linking to the person's profile
	if (url) {
		const blockCard: BlockCardDefinition = {
			type: 'blockCard',
			attrs: { url },
		};
		return blockCard;
	}

	// For fallback rendering, a people card must have at least a name to be valid
	if (!name) {
		return undefined;
	}

	// Build single paragraph with bold name followed by details
	const content: ParagraphDefinition['content'] = [
		{ type: 'text', text: name, marks: [{ type: 'strong' }] },
	];

	const details = [jobTitle, location, email].filter((d): d is string => Boolean(d));
	for (const detail of details) {
		content.push({ type: 'hardBreak' }, { type: 'text', text: detail });
	}

	return { type: 'paragraph', content };
}) as TagConverter;
export const convertReportLines: TagConverter = () => undefined;
export const convertJiraEpicProgress: TagConverter = () => undefined;
export const convertJiraBurndownChart: TagConverter = () => undefined;
export const convertJiraWorkDistribution: TagConverter = () => undefined;
